import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import SiteLayout from "@/components/site/SiteLayout";
import { api, fmtMoney } from "@/lib/api";
import { useDynamicLogo } from "@/lib/useLogo";

export default function BookingSuccessPage() {
  const [params] = useSearchParams();
  const bookingId = params.get("booking_id");
  const [status, setStatus] = useState("checking"); // checking|paid|expired|error
  const [data, setData] = useState(null);
  const attemptsRef = useRef(0);
  const logoUrl = useDynamicLogo();

  useEffect(() => {
    if (!bookingId) { setStatus("error"); return; }
    let cancelled = false;
    async function poll() {
      attemptsRef.current += 1;
      try {
        const r = await api.get(`/bookings/${bookingId}/payment-status`);
        if (cancelled) return;
        setData(r.data);
        if (r.data.payment_status === "paid") { setStatus("paid"); return; }
        if (r.data.payment_status === "failed") { setStatus("expired"); return; }
        if (attemptsRef.current >= 10) { setStatus("pending"); return; }
        setTimeout(poll, 2500);
      } catch (e) {
        if (cancelled) return;
        setStatus("error");
      }
    }
    poll();
    return () => { cancelled = true; };
  }, [bookingId]);

  return (
    <SiteLayout>
      <div className="max-w-2xl mx-auto px-4 py-24 text-center">
        <Link to="/" className="inline-block mb-10"><img src={logoUrl} alt="Berdhill Rooms" className="h-14 md:h-16" /></Link>

        {status === "checking" && (
          <div data-testid="checkout-status-checking">
            <Loader2 className="mx-auto animate-spin text-brand-primary" size={48} />
            <h1 className="font-display text-4xl mt-6">Confirming your payment…</h1>
            <p className="text-brand-muted mt-2">Please don't close this page.</p>
          </div>
        )}
        {status === "paid" && (
          <div data-testid="checkout-status-paid">
            <CheckCircle2 className="mx-auto text-brand-success" size={56} />
            <h1 className="font-display text-5xl mt-6">Reservation confirmed</h1>
            <p className="mt-4 text-brand-muted leading-relaxed">
              Thank you for choosing Berdhill Rooms. We've sent a confirmation to your email
              {data?.total && <> for <strong>{fmtMoney(data.total)}</strong></>}.
              We look forward to welcoming you in Sarandë.
            </p>

            {(data?.booking_ref || data?.pin) && (
              <div data-testid="booking-ref-card" className="mt-8 bg-gradient-to-br from-brand-secondary/60 to-brand-bg border border-brand-primary/20 rounded-md p-6 text-left">
                <div className="text-[10px] tracking-[0.3em] uppercase text-brand-muted text-center mb-4">Save these details</div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-brand-muted">Booking reference</div>
                    <div data-testid="booking-ref" className="font-mono text-2xl md:text-3xl font-semibold text-brand-text mt-1 tracking-wider">{data.booking_ref}</div>
                    <button onClick={() => { navigator.clipboard.writeText(data.booking_ref); }} className="text-[10px] text-brand-primary hover:underline mt-1">copy</button>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-brand-muted">Access PIN</div>
                    <div data-testid="booking-pin" className="font-mono text-2xl md:text-3xl font-semibold text-brand-primary mt-1 tracking-[0.2em]">{data.pin}</div>
                    <button onClick={() => { navigator.clipboard.writeText(data.pin); }} className="text-[10px] text-brand-primary hover:underline mt-1">copy</button>
                  </div>
                </div>
                <p className="text-xs text-brand-muted mt-4 leading-relaxed">
                  Keep these safe. You'll need them with your email to view or manage your booking at{" "}
                  <Link to="/manage" className="text-brand-primary underline">/manage</Link>.
                </p>
              </div>
            )}

            {data?.card_last4 && (
              <div className="mt-4 text-xs text-brand-muted">
                Paid with {data.card_brand || "card"} ····{data.card_last4}
              </div>
            )}
            <div className="mt-10 flex justify-center gap-3 flex-wrap">
              <Link to="/manage" data-testid="success-manage-link" className="bg-brand-primary text-white px-6 py-3 rounded-sm text-sm">Manage booking</Link>
              <Link to="/" className="border border-brand-border px-6 py-3 rounded-sm text-sm">Return home</Link>
            </div>
          </div>
        )}
        {status === "pending" && (
          <div>
            <Loader2 className="mx-auto text-brand-warning" size={48} />
            <h1 className="font-display text-4xl mt-6">Still processing…</h1>
            <p className="text-brand-muted mt-2">Please check your email for confirmation in a few minutes.</p>
          </div>
        )}
        {(status === "expired" || status === "error") && (
          <div data-testid="checkout-status-error">
            <XCircle className="mx-auto text-brand-error" size={56} />
            <h1 className="font-display text-4xl mt-6">Payment not completed</h1>
            <p className="text-brand-muted mt-2">Your session was cancelled or didn't complete. You can try again at any time.</p>
            <Link to="/rooms" className="inline-block mt-8 bg-brand-primary text-white px-6 py-3 rounded-sm text-sm">Back to rooms</Link>
          </div>
        )}
      </div>
    </SiteLayout>
  );
}
